import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { Period } from "../types/period";
import { getPeriods } from "../lib/api/periods";
import { useAuth } from "./AuthContext";
import { useUsers } from "./UserContext";

interface PeriodState {
  periods: Period[];
  isLoadingPeriods: boolean;
  loadedYears: Set<number>;
  loadPeriodsForYear: (year: number) => Promise<void>;
  refreshPeriods: () => Promise<void>;
  clearPeriodsCache: () => void;
}

const PeriodContext = createContext<PeriodState>({
  periods: [],
  isLoadingPeriods: false,
  loadedYears: new Set(),
  loadPeriodsForYear: async () => {},
  refreshPeriods: async () => {},
  clearPeriodsCache: () => {},
});

export function PeriodProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const { currentUser } = useUsers();
  const [periods, setPeriods] = useState<Period[]>([]);
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);
  
  // Use ref to track loaded years
  const loadedYearsRef = useRef<Set<number>>(new Set());
  const loadingYearsRef = useRef<Set<number>>(new Set());

  // Function to load periods data for a specific year
  const loadPeriodsForYear = useCallback(async (year: number) => {
    if (!token || !currentUser) {
      return;
    }

    // Check if already loaded or currently loading
    if (loadedYearsRef.current.has(year) || loadingYearsRef.current.has(year)) {
      return;
    }

    // Mark as loading
    loadingYearsRef.current.add(year);
    setIsLoadingPeriods(true);

    try {
      const periodsData = await getPeriods(currentUser.site, year);

      // Merge new periods with existing periods, removing duplicates
      setPeriods(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newPeriods = periodsData.periods?.filter(p => !existingIds.has(p.id)) || [];
        return [...prev, ...newPeriods];
      });

      // Mark this year as loaded
      loadedYearsRef.current.add(year);
    } catch (error) {
      console.warn(`Failed to load periods for year ${year}:`, error);
    } finally {
      // Remove from loading set
      loadingYearsRef.current.delete(year);
      setIsLoadingPeriods(loadingYearsRef.current.size > 0);
    }
  }, [token, currentUser]);

  // Refresh all previously loaded years
  const refreshPeriods = useCallback(async () => {
    if (!token || !currentUser) return;

    const yearsToReload = Array.from(loadedYearsRef.current);
    if (yearsToReload.length === 0) return;

    setIsLoadingPeriods(true);

    try {
      const periodsPromises = yearsToReload.map(year =>
        getPeriods(currentUser.site, year)
      );

      const periodsResults = await Promise.all(periodsPromises);

      // Merge all periods, removing duplicates
      const allPeriods = periodsResults.flatMap(result => result.periods || []);
      const uniquePeriods = Array.from(
        new Map(allPeriods.map(p => [p.id, p])).values()
      );

      setPeriods(uniquePeriods);
    } catch (err) {
      console.error("Failed to refresh periods:", err);
    } finally {
      setIsLoadingPeriods(false);
    }
  }, [token, currentUser]);

  // Clear cache
  const clearPeriodsCache = useCallback(() => {
    setPeriods([]);
    loadedYearsRef.current.clear();
    loadingYearsRef.current.clear();
    setIsLoadingPeriods(false);
  }, []);

  // Reset state when token or current user changes
  useEffect(() => {
    if (!token || !currentUser) {
      clearPeriodsCache();
    }
  }, [token, currentUser, clearPeriodsCache]);

  return (
    <PeriodContext.Provider
      value={{
        periods,
        isLoadingPeriods,
        loadedYears: loadedYearsRef.current,
        loadPeriodsForYear,
        refreshPeriods,
        clearPeriodsCache,
      }}
    >
      {children}
    </PeriodContext.Provider>
  );
}

export function usePeriods() {
  const context = useContext(PeriodContext);
  if (!context) {
    throw new Error("usePeriods must be used within a PeriodProvider");
  }
  return context;
}
